// いつでも相談の添削リクエスト取得をポーリングするcomposable

// 役割
// - AnytimeConsultationCorrectionTab.vue から参照され、anytime_consultation_corrections.ts と協調し、ActionCable接続が切断したときのポーリングを管理する
// 機能
// - ActionCable接続が切断したとき(handleChannelDisconnected, handleChannelRejected)、2秒間隔でポーリングを開始する
// - 全ての添削メニューが進行中ステータスではなくなった時、もしくは再接続したときにポーリングを停止する

const CORRECTIONS_POLLING_INTERVAL_MS = 2_000;

export interface CorrectionsPollingParams {
  consultationId: number;
}

export interface CorrectionsPollingConnectionHandlers {
  handleChannelConnected: (params: { consultationId: number; isReconnect: boolean }) => void;
  handleChannelReceived: (params: { consultationId: number }) => void;
  handleChannelDisconnected: (params: { consultationId: number }) => void;
  handleChannelRejected: (params: { consultationId: number }) => void;
}

interface CorrectionsPollingHandlersArgs {
  hasInProgressCorrections: () => boolean;
  fetchCorrectionsData: (consultationId: number) => Promise<void>;
}

export const createConsultationCorrectionsPolling = ({
  hasInProgressCorrections,
  fetchCorrectionsData,
}: CorrectionsPollingHandlersArgs) => {
  let pollingTimerId: ReturnType<typeof setTimeout> | null = null;
  let isPolling = false;

  const handlers: {
    startPolling: (params: CorrectionsPollingParams) => void;
    stopPolling: () => void;
    pollOnce: (params: CorrectionsPollingParams) => Promise<void>;
  } & CorrectionsPollingConnectionHandlers = {
    startPolling: ({ consultationId }: CorrectionsPollingParams) => {
      if (!hasInProgressCorrections()) return;
      if (isPolling) return;

      isPolling = true;
      void handlers.pollOnce({ consultationId });
    },
    stopPolling: () => {
      if (pollingTimerId !== null) {
        clearTimeout(pollingTimerId);
        pollingTimerId = null;
      }

      isPolling = false;
    },
    pollOnce: async ({ consultationId }: CorrectionsPollingParams) => {
      if (!isPolling) return;

      if (pollingTimerId !== null) {
        clearTimeout(pollingTimerId);
        pollingTimerId = null;
      }

      await fetchCorrectionsData(consultationId);

      if (!hasInProgressCorrections()) {
        handlers.stopPolling();
        return;
      }

      if (!isPolling) return;

      pollingTimerId = setTimeout(() => {
        void handlers.pollOnce({ consultationId });
      }, CORRECTIONS_POLLING_INTERVAL_MS);
    },
    handleChannelConnected: ({ isReconnect }) => {
      if (isReconnect) {
        handlers.stopPolling();
      }
    },
    handleChannelReceived: () => {
      handlers.stopPolling();
    },
    handleChannelDisconnected: ({ consultationId }) => {
      console.log("AnytimeConsultationChannel disconnected. ポーリングを開始します。");
      handlers.startPolling({ consultationId });
    },
    handleChannelRejected: ({ consultationId }) => {
      console.log("AnytimeConsultationChannel rejected. ポーリングを開始します。");
      handlers.startPolling({ consultationId });
    },
  };

  return handlers;
};
